/* Quiz Café / Thé — state machine + profil éditorial + recommandation dynamique
   Config : { meta: {gamme, nbAlternatives, questions[], profiles[]}, products[] }
   - meta : généré par DasiApp (snippet quiz-data-<gamme>), seed d'exemple par défaut.
   - products : sérialisés par la section avec leurs signaux metafields. */
(function () {
  'use strict';

  const root = document.querySelector('.quiz-section[data-quiz]');
  if (!root) return;
  // Garde d'idempotence : quiz.js peut être chargé plusieurs fois (theme.liquid
  // + section). Sans ça, deux machines à états se partagent le DOM et le
  // résultat ne compte plus que la dernière question. On n'initialise qu'une fois.
  if (root.dataset.quizInit === '1') return;
  root.dataset.quizInit = '1';
  const configEl = root.querySelector('[data-quiz-config]');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[quiz] config JSON invalide', e);
    return;
  }
  const meta = config.meta || {};
  const products = Array.isArray(config.products) ? config.products : [];
  const questions = Array.isArray(meta.questions) ? meta.questions : [];
  const profiles = Array.isArray(meta.profiles) ? meta.profiles : [];
  const gamme = meta.gamme || '';
  const nbAlt = meta.nbAlternatives != null ? meta.nbAlternatives : 2;
  if (!questions.length || !profiles.length) {
    console.warn('[quiz] config incomplète (questions/profils manquants)');
    return;
  }

  const screens = {
    intro: root.querySelector('[data-screen="intro"]'),
    question: root.querySelector('[data-screen="question"]'),
    results: root.querySelector('[data-screen="results"]'),
  };
  const startBtn = root.querySelector('[data-start]');
  const restartBtn = root.querySelector('[data-restart]');
  const backBtn = root.querySelector('[data-back]');
  const nextBtn = root.querySelector('[data-next]');
  const progressEl = root.querySelector('[data-progress]');
  const stepEl = root.querySelector('[data-step]');
  const questionEl = root.querySelector('[data-question]');
  const answersEl = root.querySelector('[data-answers]');
  const resultEl = root.querySelector('[data-result]');

  const state = { current: 0, answers: [], done: false };

  // Mode debug : réglage de section (data-quiz-debug) OU ?quizdebug=1 dans l'URL.
  // Jamais actif pour les clients par défaut (réglage décoché + pas de paramètre).
  const debugMode =
    (root.dataset && root.dataset.quizDebug === 'true') ||
    /[?&]quizdebug=1(?:&|$)/.test(location.search || '');

  // Sauvegarde du quiz dans le navigateur (par gamme) : au retour sur la page,
  // on retrouve son résultat / sa question au lieu de tout recommencer.
  const STORAGE_KEY = 'dasi-quiz-' + (gamme || 'cafe');
  function saveState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ answers: state.answers, current: state.current, done: state.done }));
    } catch (e) { /* sessionStorage indisponible */ }
  }
  function loadState() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { return null; }
  }
  function clearState() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
  }

  function showScreen(name, opts) {
    Object.entries(screens).forEach(([key, el]) => {
      if (el) el.hidden = key !== name;
    });
    if (!opts || opts.scroll !== false) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ---------- Questions ----------
  // Nombre de colonnes (2 ou 3) évitant une dernière ligne à un seul élément.
  function pickCols(n) {
    if (n <= 3) return n; // 1,2,3 → une seule ligne
    if (n % 3 !== 1) return 3; // 5→3+2, 6→3+3, 8→3+3+2
    if (n % 2 !== 1) return 2; // 4→2+2
    return 3; // repli (n impair non divisible, ex. 7) : dernière ligne centrée
  }

  function renderQuestion() {
    const q = questions[state.current];
    if (!q) return;
    const total = questions.length;
    stepEl.textContent = `Question ${state.current + 1} sur ${total}`;
    progressEl.style.width = `${((state.current + 1) / total) * 100}%`;
    questionEl.textContent = q.label;

    const isMulti = q.multi === true || q.multi === 'true';
    const selected = state.answers[state.current] || [];
    const answers = Array.isArray(q.answers) ? q.answers : [];

    // Colonnes : 2 ou 3 max, jamais une dernière ligne à 1 (« pas de 3+1 »).
    // 4→2×2, 5→3+2, 6→3×2, 3→3. La dernière ligne incomplète est centrée (CSS).
    answersEl.style.setProperty('--quiz-ans-cols', String(pickCols(answers.length)));

    answersEl.innerHTML = '';
    answers.forEach((a, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-answer';
      btn.dataset.index = String(i);
      if (selected.includes(i)) btn.classList.add('selected');
      btn.innerHTML = `<span class="quiz-answer__check"></span><span>${escapeHtml(a.label)}</span>`;
      btn.addEventListener('click', () => {
        if (isMulti) {
          btn.classList.toggle('selected');
        } else {
          answersEl.querySelectorAll('.quiz-answer').forEach((x) => x.classList.remove('selected'));
          btn.classList.add('selected');
        }
        updateNextBtn();
      });
      answersEl.appendChild(btn);
    });

    backBtn.hidden = state.current === 0;
    const isLast = state.current === questions.length - 1;
    const arrow = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
    nextBtn.innerHTML = (isLast ? 'Voir mon résultat ' : 'Continuer ') + arrow;
    updateNextBtn();

    // Rejoue l'animation de transition à chaque nouvelle question.
    [questionEl, answersEl].forEach((el) => {
      if (!el) return;
      el.classList.remove('quiz-anim-in');
      void el.offsetWidth;
      el.classList.add('quiz-anim-in');
    });
  }

  function updateNextBtn() {
    nextBtn.disabled = answersEl.querySelectorAll('.quiz-answer.selected').length === 0;
    renderLiveDebug();
  }

  function saveAnswers() {
    state.answers[state.current] = Array.from(answersEl.querySelectorAll('.quiz-answer.selected'))
      .map((el) => parseInt(el.dataset.index, 10));
  }

  // ---------- Profil ----------
  // Additionne les poids des réponses → scores par profil. `answersArr` permet
  // de calculer un score « en direct » (ex. sélection en cours, pas encore
  // validée) ; par défaut on prend l'état complet des réponses.
  function computeScores(answersArr) {
    const src = answersArr || state.answers;
    const scores = {};
    let forceCapsule = false;
    profiles.forEach((p) => { scores[p.code] = 0; });

    questions.forEach((q, qi) => {
      (src[qi] || []).forEach((ai) => {
        const a = (q.answers || [])[ai];
        if (!a) return;
        if (a.weights) {
          Object.keys(a.weights).forEach((code) => {
            if (code in scores) scores[code] += Number(a.weights[code]) || 0;
          });
        }
        if (Array.isArray(a.flags) && a.flags.indexOf('format:capsule') !== -1) forceCapsule = true;
      });
    });
    return { scores: scores, forceCapsule: forceCapsule };
  }

  function computeProfile() {
    // Le profil = TOUJOURS le goût (score max + priority). Le choix « capsule »
    // (flag) n'influe PAS sur le profil : il ne fait que trier les produits
    // (capsules en premier), géré dans selectProducts.
    const r = computeScores();
    let best = profiles[0];
    let bestScore = -Infinity;
    profiles.forEach((p) => {
      const s = r.scores[p.code] || 0;
      const pr = p.priority != null ? p.priority : 999;
      const bpr = best.priority != null ? best.priority : 999;
      if (s > bestScore || (s === bestScore && pr < bpr)) {
        best = p;
        bestScore = s;
      }
    });
    return best;
  }

  // Profil de tête pour un dictionnaire de scores (même départage que le moteur).
  function leaderCode(scores) {
    let best = null;
    let bestScore = -Infinity;
    profiles.forEach((p) => {
      const s = scores[p.code] || 0;
      const pr = p.priority != null ? p.priority : 999;
      const bpr = best ? (best.priority != null ? best.priority : 999) : 999;
      if (best === null || s > bestScore || (s === bestScore && pr < bpr)) {
        best = p;
        bestScore = s;
      }
    });
    return best ? best.code : null;
  }

  // ---------- Debug live (pendant le quiz) ----------
  // Scores « en direct » = réponses déjà validées + sélection en cours (non
  // encore enregistrée) de la question affichée.
  function liveScores() {
    const clone = state.answers.slice();
    if (answersEl) {
      clone[state.current] = Array.from(
        answersEl.querySelectorAll('.quiz-answer.selected'),
      ).map((el) => parseInt(el.dataset.index, 10));
    }
    return computeScores(clone);
  }

  let liveDebugEl = null;
  function renderLiveDebug() {
    if (!debugMode) return;
    if (!liveDebugEl) {
      liveDebugEl = document.createElement('div');
      liveDebugEl.className = 'quiz-live-debug';
      if (answersEl && answersEl.parentNode) {
        answersEl.parentNode.insertBefore(liveDebugEl, answersEl.nextSibling);
      }
    }
    const r = liveScores();
    const scores = r.scores;
    const maxScore = Math.max(1, ...profiles.map((p) => scores[p.code] || 0));
    const lead = leaderCode(scores);
    const rows = profiles
      .slice()
      .sort((a, b) => (scores[b.code] || 0) - (scores[a.code] || 0))
      .map((p) => {
        const s = scores[p.code] || 0;
        const pct = Math.round((s / maxScore) * 100);
        const isLead = p.code === lead && s > 0;
        return (
          '<div class="quiz-live-debug__row">' +
          '<span class="quiz-live-debug__name">' + (isLead ? '★ ' : '') + escapeHtml(p.name || p.code) + '</span>' +
          '<span class="quiz-live-debug__bar"><span class="quiz-live-debug__fill' + (isLead ? ' is-lead' : '') + '" style="width:' + pct + '%"></span></span>' +
          '<span class="quiz-live-debug__val">' + s + '</span>' +
          '</div>'
        );
      })
      .join('');
    const capNote = r.forceCapsule
      ? '<p class="quiz-live-debug__note">Choix « capsule » coché → seuls les produits capsule seront affichés (le profil reste celui du goût).</p>'
      : '';
    liveDebugEl.innerHTML =
      '<p class="quiz-live-debug__title">🐞 Debug live — points par profil (mis à jour à chaque réponse)</p>' +
      rows + capNote;
  }

  // ---------- Produits ----------
  function theineKey(t) {
    const s = String(t || '').toLowerCase();
    if (!s) return '';
    if (s.indexOf('sans') !== -1) return 'sans';
    if (s.indexOf('élev') !== -1 || s.indexOf('elev') !== -1) return 'haute';
    if (s.indexOf('mod') !== -1) return 'moyenne';
    if (s.indexOf('faible') !== -1) return 'basse';
    return s;
  }

  function deriveTheine(p) {
    if (p.theine) return theineKey(p.theine);
    const b = String(p.base || '').toLowerCase();
    if (b === 'rooibos' || b === 'carcadet' || b === 'tisane') return 'sans';
    if (b === 'noir') return 'haute';
    if (b === 'vert' || b === 'blanc' || b === 'oolong' || b === 'mate') return 'moyenne';
    return '';
  }

  function scoreProduct(p, sig) {
    let s = 0;
    const pAromes = (p.aromes || []).map((x) => String(x).toLowerCase());
    const noteDom = String(p.noteDominante || '').toLowerCase();
    (sig.aromes || []).forEach((aRaw) => {
      const a = String(aRaw).toLowerCase().trim();
      if (!a) return;
      if (pAromes.some((x) => x.indexOf(a) !== -1 || a.indexOf(x) !== -1) || (noteDom && noteDom.indexOf(a) !== -1)) s += 2;
    });
    const intens = parseInt(p.intensite, 10);
    if (!isNaN(intens) && sig.intensiteMin != null && sig.intensiteMax != null && intens >= sig.intensiteMin && intens <= sig.intensiteMax) s += 2;
    if (sig.format === 'capsule' && p.hasCapsule) s += 3;
    const bases = (sig.bases || []).map((x) => String(x).toLowerCase());
    if (bases.length && p.base && bases.indexOf(String(p.base).toLowerCase()) !== -1) s += 3;
    if (sig.theine && deriveTheine(p) === theineKey(sig.theine)) s += 1;
    const pTags = (p.tags || []).map((t) => String(t).toLowerCase());
    (sig.tagsBonus || []).forEach((tRaw) => {
      const t = String(tRaw).toLowerCase().trim();
      if (t && pTags.some((x) => x.indexOf(t) !== -1)) s += 1;
    });
    return s;
  }

  // featured peut être une chaîne (ancien format, 1 produit) OU un tableau
  // ({handle,title} ou chaîne). On normalise en liste de handles.
  function featuredHandles(profile) {
    const f = profile.featured;
    if (Array.isArray(f)) return f.map((x) => (typeof x === "string" ? x : x && x.handle)).filter(Boolean);
    if (typeof f === "string" && f) return [f];
    return [];
  }

  // Produit « capsule » : metafield hasCapsule, ou type/titre contenant capsule.
  function isCapsuleProduct(p) {
    return !!(
      p.hasCapsule ||
      /capsule/i.test(String(p.type || '')) ||
      /capsule/i.test(String(p.title || ''))
    );
  }

  function selectProducts(profile, forceCapsule) {
    const pool = products.filter((p) => !gamme || p.gamme === gamme);
    const sig = profile.signature || {};
    // Mode capsule : profil dédié (format capsule) OU choix « Capsules » coché
    // au quiz (flag) → on met les produits capsule en tête, même si le profil
    // gagnant n'est pas un profil capsule.
    const capsuleMode = sig.format === 'capsule' || forceCapsule === true;

    // Produits mis en avant (curés) : on garde l'ordre, on résout par handle.
    // En mode capsule, on n'affiche QUE les vedettes capsule (les autres masquées).
    let featured = featuredHandles(profile)
      .map((h) => pool.find((p) => p.handle === h) || products.find((p) => p.handle === h))
      .filter(Boolean);
    if (capsuleMode) {
      featured = featured.filter(isCapsuleProduct);
    }
    const featuredIds = {};
    featured.forEach((p) => { featuredIds[p.id] = true; });

    const scored = pool
      .filter((p) => !featuredIds[p.id])
      .map((p) => ({ p: p, s: scoreProduct(p, sig) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s);

    let alts = scored.map((x) => x.p);
    if (alts.length < nbAlt) {
      pool.forEach((p) => {
        if (!featuredIds[p.id] && !alts.some((a) => a.id === p.id)) alts.push(p);
      });
    }
    // Mode capsule : uniquement des produits capsule dans les alternatives aussi.
    if (capsuleMode) {
      alts = alts.filter(isCapsuleProduct);
    }
    alts = alts.slice(0, nbAlt);
    return { featured: featured, alternatives: alts };
  }

  // ---------- Rendu résultat ----------
  // Note sous le titre, format identique aux cartes natives :
  //  - café → « Note aromatique : … » (casse normale)
  //  - thé  → « TYPE | … » (type de produit + notes, en majuscules via CSS)
  function noteLine(p) {
    const nd = String(p.noteDominante || '').trim();
    if (!nd) return '';
    if (gamme === 'the') {
      const type = String(p.type || '').trim();
      return `<p class="dasi-card-note">${type ? escapeHtml(type) + ' | ' : ''}${escapeHtml(nd)}</p>`;
    }
    return `<p class="dasi-card-note dasi-card-note--cafe"><span class="dasi-card-note__label">Note aromatique :</span> ${escapeHtml(nd)}</p>`;
  }

  // Prix + suffixe conditionnement (« 8,50 € | pour 100g vrac »).
  function priceHtml(p) {
    const cond = String(p.conditionnement || '').trim();
    const suffix = cond
      ? ` <span class="price-conditionnement"><span class="price-conditionnement__sep" aria-hidden="true">|</span> pour ${escapeHtml(cond)}</span>`
      : '';
    return `<span class="price">${p.priceFormatted || ''}</span>${suffix}`;
  }

  // Badges (tags_badges) — mêmes classes que la carte native (couleurs via tag-colors).
  // Même structure que la fiche produit (snippet tag-badge-item) : icône
  // optionnelle (tag-badge__icon) + libellé, mêmes classes → rendu identique
  // (CSS product-tag-badges.css partagé, chargé par la section).
  function badgesHtml(p) {
    const badges = Array.isArray(p.badges) ? p.badges : [];
    if (!badges.length) return '';
    return '<div class="tag-badges tag-badges--card">' +
      badges.slice(0, 3).map((b) => {
        const icon = b.icon
          ? '<img class="tag-badge__icon" src="' + b.icon + '" alt="" loading="lazy" width="16" height="16">'
          : '';
        return '<span class="tag-badge tag-badge--' + (b.handle || '') + '">' + icon + escapeHtml(b.label || '') + '</span>';
      }).join('') +
      '</div>';
  }

  // Carte produit au look de la grille « recommandés » (version légère).
  function productCard(p, isFeatured) {
    return `
      <a class="quiz-card${isFeatured ? ' quiz-card--featured' : ''}" href="${p.url}">
        <div class="quiz-card__media">
          ${isFeatured ? '<span class="quiz-card__badge">Notre choix</span>' : ''}
          ${p.image ? `<img src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
        </div>
        ${badgesHtml(p)}
        <h3 class="quiz-card__title">${escapeHtml(p.title)}</h3>
        ${noteLine(p)}
        <div class="quiz-card__price">${priceHtml(p)}</div>
      </a>`;
  }

  function ctaHref(profile, featured) {
    if (profile.ctaUrl) return profile.ctaUrl;
    if (featured && featured.url) return featured.url;
    return '#';
  }

  function renderResults() {
    state.done = true;
    saveState();
    const forceCapsule = computeScores().forceCapsule;
    const profile = computeProfile();
    const picks = selectProducts(profile, forceCapsule);
    const cards = [];
    picks.featured.forEach((p, i) => cards.push(productCard(p, i === 0)));
    picks.alternatives.forEach((p) => cards.push(productCard(p, false)));
    const arrow = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

    resultEl.innerHTML = `
      <p class="quiz-result__eyebrow">Votre profil</p>
      <h2 class="quiz-result__name">${escapeHtml(profile.name || '')}</h2>
      ${profile.accroche ? `<p class="quiz-result__accroche">${escapeHtml(profile.accroche)}</p>` : ''}
      ${profile.texte ? `<p class="quiz-result__texte">${escapeHtml(profile.texte)}</p>` : ''}
      ${profile.phraseTorrefacteur ? `<blockquote class="quiz-result__torrefacteur">${escapeHtml(profile.phraseTorrefacteur)}</blockquote>` : ''}
      <div class="quiz-results-grid">${cards.join('')}</div>
      ${profile.ctaLabel ? `<a class="quiz-section__cta quiz-result__cta" href="${ctaHref(profile, picks.featured[0])}">${escapeHtml(profile.ctaLabel)} ${arrow}</a>` : ''}
    `;

    // Centrage intelligent : autant de colonnes que de cartes (max 4) → 1, 2 ou 3 restent centrées.
    const grid = resultEl.querySelector('.quiz-results-grid');
    if (grid) grid.style.setProperty('--quiz-cols', String(Math.min(cards.length, 4)));

    if (debugMode) resultEl.insertAdjacentHTML('beforeend', debugPanelHtml(profile));
  }

  // ---------- Debug : écart de points entre profils (interne) ----------
  function debugPanelHtml(winner) {
    const r = computeScores();
    const scores = r.scores;
    // Classement par score décroissant (départage par priority, comme le moteur).
    const ranked = profiles.slice().sort((a, b) => {
      const d = (scores[b.code] || 0) - (scores[a.code] || 0);
      if (d !== 0) return d;
      return (a.priority != null ? a.priority : 999) - (b.priority != null ? b.priority : 999);
    });
    const winScore = scores[winner.code] || 0;
    // Profil non-gagnant le plus proche (écart minimal au gagnant).
    let closest = null;
    ranked.forEach((p) => {
      if (p.code === winner.code) return;
      const gap = winScore - (scores[p.code] || 0);
      if (closest === null || gap < closest.gap) closest = { p: p, gap: gap };
    });

    const rows = ranked.map((p) => {
      const s = scores[p.code] || 0;
      const gap = winScore - s;
      const isWin = p.code === winner.code;
      return (
        '<tr style="' + (isWin ? 'font-weight:700;' : '') + '">' +
        '<td style="padding:2px 8px;">' + (isWin ? '★ ' : '') + escapeHtml(p.name || p.code) + '</td>' +
        '<td style="padding:2px 8px;text-align:right;">' + s + ' pts</td>' +
        '<td style="padding:2px 8px;text-align:right;color:#6a5a4b;">' + (isWin ? '—' : 'à ' + gap + ' pt' + (gap > 1 ? 's' : '')) + '</td>' +
        '</tr>'
      );
    }).join('');

    const capsuleNote = r.forceCapsule
      ? '<p style="margin:6px 0 0;color:#842c18;">Choix « Capsules » coché → seuls les produits capsule sont affichés (le profil reste celui du goût).</p>'
      : '';
    const closestNote = closest
      ? '<p style="margin:6px 0 0;">Il manquait <strong>' + closest.gap + ' pt' + (closest.gap > 1 ? 's' : '') + '</strong> à « ' + escapeHtml(closest.p.name || closest.p.code) + ' » pour l\'emporter.</p>'
      : '';

    return (
      '<div class="quiz-debug" style="margin-top:24px;padding:12px 14px;border:1px dashed #c9b8a8;border-radius:10px;background:#faf7f3;font-size:13px;text-align:left;">' +
      '<p style="margin:0 0 6px;font-weight:700;">🐞 Debug scoring (non visible par les clients)</p>' +
      '<table style="border-collapse:collapse;width:100%;">' + rows + '</table>' +
      closestNote + capsuleNote +
      '</div>'
    );
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ---------- Events ----------
  if (startBtn) {
    startBtn.addEventListener('click', () => {
      state.current = 0;
      state.answers = [];
      state.done = false;
      clearState();
      renderQuestion();
      showScreen('question');
      saveState();
    });
  }
  backBtn.addEventListener('click', () => {
    saveAnswers();
    if (state.current > 0) {
      state.current -= 1;
      renderQuestion();
    }
    saveState();
  });
  nextBtn.addEventListener('click', () => {
    saveAnswers();
    if (state.current < questions.length - 1) {
      state.current += 1;
      renderQuestion();
      saveState();
    } else {
      renderResults();
      showScreen('results');
    }
  });
  if (restartBtn) {
    restartBtn.addEventListener('click', () => {
      state.current = 0;
      state.answers = [];
      state.done = false;
      clearState();
      showScreen('intro');
    });
  }

  // Restauration au chargement (retour navigateur, rechargement) : on reprend
  // là où on s'était arrêté — résultats si terminé, sinon la question en cours.
  (function restore() {
    const saved = loadState();
    if (!saved || !Array.isArray(saved.answers) || saved.answers.length === 0) return;
    state.answers = saved.answers;
    if (saved.done) {
      renderResults();
      showScreen('results', { scroll: false });
    } else {
      state.current = Math.min(saved.current || 0, questions.length - 1);
      renderQuestion();
      showScreen('question', { scroll: false });
    }
  })();
})();
